/**
 * Shared form styling for dialogs and drawers.
 *
 * One definition of what a form control looks like, so every modal matches the Add New User
 * drawer (the reference form). Previously this string was duplicated verbatim across the billing
 * dialog, the rename modal, the team drawer and the quote form, and every other modal invented its
 * own — twelve different label styles and three different field heights across the app.
 *
 * Every value is token-backed, so forms follow the `dark` class on <html> without branching on the
 * mode. Never a raw hex here — see theme.css "Portal design tokens".
 */

/**
 * Text/email/tel inputs and anything shaped like one (native selects, textareas).
 *
 * A Stripe card field cannot take these classes — it is an iframe — so its matching shape lives
 * in the StripeField primitive next door. Change the height, radius or fill here and change it
 * there too, or the card fields drift away from the inputs beside them.
 */
export const fieldClassName =
  'h-11 rounded-lg border-line-input bg-surface-filter text-fg placeholder:text-fg-placeholder';

/** Same as `fieldClassName` but without the fixed height, for multi-line controls. */
export const textareaClassName =
  'rounded-lg border-line-input bg-surface-filter text-fg placeholder:text-fg-placeholder';

/**
 * Field labels. The Label primitive supplies no colour of its own, so this is the single place a
 * label's ink is decided — `--fg-label` (#6c6c6c light, --fg-secondary dark). Anything that reads
 * as a control's label should wear this rather than spelling out a size/weight/colour of its own.
 */
export const labelClassName = 'text-sm font-medium text-fg-label';

/** Validation and server-error messages sitting under a field. */
export const errorTextClassName = 'text-sm text-danger-fg';

/**
 * The same error style for a DropdownOptions, passed as its `errorClassName` (the dropdown's own
 * default is smaller). The margin matches the gap the dropdown leaves above its error line.
 */
export const dropdownErrorClassName = `${errorTextClassName} mt-1`;

/**
 * The label beside a checkbox ("Same as billing address", "Set as primary"). Unlike a field label
 * it reads as body text, and it is clickable — it toggles the box.
 */
export const checkboxLabelClassName = 'text-sm font-normal text-fg cursor-pointer';

/** Helper/hint text under a field or beside a heading. */
export const hintTextClassName = 'text-xs text-fg-muted';

/** Section headings inside a modal body ("Elevated permissions", "Subscription & Product"). */
export const sectionHeadingClassName = 'text-lg font-bold text-fg mb-4';

/** Applied to a field when its value failed validation, alongside `fieldClassName`. */
export const fieldErrorClassName = 'border-danger-fg focus:border-danger-fg';

/*
 * Footer actions for dialogs and drawers. Kept here for the same reason as the field styles: five
 * drawers had five different footers, with four different button heights (36/40/44/50px) and one
 * using a filled `secondary` Cancel where everywhere else uses `outline`.
 *
 * Stacks on a narrow viewport with the primary action on top (`flex-col-reverse`), so the button a
 * user most likely wants is nearest their thumb.
 *
 * `[&_button]:h-12 [&_button]:text-base` sets the action size — 48px tall, 16px label — for footers
 * written as a plain <div> rather than a DialogFooter/SheetFooter (several drawers do that to
 * escape the primitive's padding); the two primitives carry the identical rules, so a footer gets
 * them either way, including on the bare `<Button>`s that never took the two classes below.
 */
export const dialogFooterClassName =
  'flex flex-col-reverse sm:flex-row justify-end gap-2 sm:gap-3 border-t border-line-field px-4 sm:px-6 py-3 sm:py-4 [&_button]:h-12 [&_button]:text-base';

/** The dismissing action. Pair with `variant="outline"`. */
export const cancelButtonClassName =
  'h-12 px-4 sm:px-6 rounded-xl border-line-field bg-surface-panel text-fg-control hover:bg-surface-hover font-medium text-base';

/**
 * The confirming action. Takes no background — the Button default variant supplies --brand, so a
 * primary button is never re-coloured at the call site.
 */
export const primaryButtonClassName =
  'h-12 px-4 sm:px-6 rounded-xl font-medium text-white text-base';

/**
 * A card's border and background — the dashboard card's, shared so every card in the app takes the
 * same two colours. Colours only: layout, padding, radius and shadow stay with each card.
 */
export const cardSurfaceClassName = 'border-line-card bg-surface-panel';

/**
 * A secondary, in-content action — the dashboard's "Manage" look: neutral border, plain text, soft
 * hover fill. Pair with `variant="outline"`. For buttons that sit inside a card or section, not in
 * a dialog footer (that is cancelButtonClassName).
 */
export const secondaryButtonClassName =
  'h-10 rounded-[12px] border-line-control text-fg hover:bg-surface-hover';
